// src/modules/application/pages/ProbationerApplicationWizard.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { LogOut, ArrowLeft, ArrowRight, ClipboardList } from "lucide-react";
import { useAuth } from "../../../context/AuthContexts";
import {
  useAcknowledgeProbationerApplicationMutation,
  useCreateProbationerApplicationStep1Mutation,
  useGetActiveUserCategoriesQuery,
  useGetMyProbationerApplicationsQuery,
  useGetProbationerApplicationDetailsQuery,
  useSaveProbationerStep2QualificationsMutation,
  useSaveProbationerStep3OLevelMutation,
  useSaveProbationerStep4MembershipsMutation,
  useSaveProbationerStep5ExperienceMutation,
  useSaveProbationerStep6SeminarsMutation,
  useSaveProbationerStep7RefereesMutation,
} from "../api/probationerApplicationApi";
import { useLazyMeQuery } from "../../auth/api/authApi";

import { clearDraft, loadDraft, saveDraft } from "../utils/applicationDraft";

import Step1Bio, { Step1BioInitial } from "../components/steps/Step1Bio";
import Step2Qualifications, {
  QualificationRow,
} from "../components/steps/Step2Qualifications";
import Step3OLevel, {
  Step3OLevelInitial,
} from "../components/steps/Step3OLevel";
import Step4Memberships, {
  Step4MembershipRow,
} from "../components/steps/Step4Memberships";
import Step5Experience, {
  Step5ExperienceRow,
} from "../components/steps/Step5Experience";
import Step6Seminars, {
  Step6SeminarRow,
} from "../components/steps/Step6Seminars";
import Step7Referees, {
  Step7RefereeInitial,
  SelectedMember,
} from "../components/steps/Step7Referees";
import Step8Acknowledge from "../components/steps/Step8Acknowledge";
import { IN_REVIEW } from "../utils/applicationStatus";

// =========================
// TYPES
// =========================
type AppStatus =
  | "pending"
  | "acknowledged"
  | "awaiting_endorsements"
  | "under_review"
  | "with_committee"
  | "approved"
  | "rejected"
  | "submitted"
  | "returned"
  | string;

type ApplicationSummary = {
  id: string;
  status?: AppStatus;
  completion_step?: number;
  title?: string;
  surname?: string;
  other_names?: string;
  created_at?: string;
};

type ApplicationDetails = {
  id: string;
  status?: AppStatus;
  completion_step?: number;

  surname?: string;
  other_names?: string;
  title?: string;
  gender?: string;
  postal_address?: string;
  residential_address?: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  nationality?: string;

  application_category_id?: number;
  application_category?: { id: number; name: string };

  state_id?: number;
  lga_id?: number;
  local_government_id?: number;
  sector_id?: number;
  chapter_id?: string | number | null;
  chapter_other?: string;

  pro_pic?: string;
  signature?: string;
  pro_pic_url?: string;
  signature_url?: string;

  qualifications?: Array<{
    id?: string;
    institution?: string;
    qualification?: string;
    year?: string | number;
    cert_url?: string;
    certificate_url?: string;
    certificate?: string;
  }>;

  o_level_results?: Array<{
    id?: string;
    exam_type?: "waec" | "neco" | "nabteb";
    exam_year?: string | number;
    exam_number?: string;
    subjects?: Record<string, string>;
  }>;

  professional_memberships?: Array<{
    id?: string;
    body_name?: string;
    stage_passed?: string;
    certificate_path?: string;
  }>;

  work_experiences?: Array<{
    id?: string;
    organization?: string;
    position?: string;
    start_date?: string;
    end_date?: string;
    responsibilities?: string;
  }>;

  seminars?: Array<{
    id?: string;
    title?: string;
    date?: string;
    location?: string;
  }>;

  referees?: Array<{
    id?: string;
    member_id?: string;
    relationship?: string;
    member?: {
      id: string;
      surname?: string;
      other_names?: string;
      email?: string;
      phone?: string;
    };
  }>;

  created_at?: string;
};

const STORAGE_BASE = "https://api.niqsng.org/storage";

function toStorageUrl(path?: string | null): string {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${STORAGE_BASE}/${path.replace(/^\//, "")}`;
}

function safeMsg(err: any): string | null {
  // RTK Query internal abort (component unmount / subscription cleanup).
  // Not a user-facing error — swallow it silently.
  if (
    err?.name === "AbortError" ||
    err?.error === "AbortError" ||
    err?.data?.error === "AbortError"
  ) {
    return null;
  }

  const d = err?.data ?? err;

  // Laravel validation error format: { message, errors: {field:[msg]} }
  if (d?.errors && typeof d.errors === "object") {
    const firstKey = Object.keys(d.errors)[0];
    const firstMsg = Array.isArray(d.errors[firstKey])
      ? d.errors[firstKey][0]
      : String(d.errors[firstKey]);
    return firstMsg || d?.message || "Validation failed";
  }

  return String(d?.message || d?.error || err?.error || "Something went wrong");
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}


export default function ProbationerApplicationWizard() {
  const navigate = useNavigate();
  const { signOut, user } = useAuth();

  // load draft for currentStep + applicationId
  const draft = useMemo(() => loadDraft(), []);
  const [currentStep, setCurrentStep] = useState<number>(
    draft.currentStep || 1,
  );
  const [applicationId, setApplicationId] = useState<string | undefined>(
    draft.applicationId,
  );

  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);

  // ✅ Step1 categories
  const {
    data: categories = [],
    isFetching: categoriesFetching,
    error: categoriesError,
  } = useGetActiveUserCategoriesQuery();

  const categoriesErrMsg =
    (categoriesError as any)?.data?.message ||
    (categoriesError as any)?.error ||
    null;

  // list endpoint
  const {
    data: appsRaw,
    isFetching: appsFetching,
    refetch,
  } = useGetMyProbationerApplicationsQuery();

  // ✅ support list shapes: [] OR {data: []}
  const apps: ApplicationSummary[] = useMemo(() => {
    if (Array.isArray(appsRaw)) return appsRaw as any;
    if (Array.isArray((appsRaw as any)?.data)) return (appsRaw as any).data;
    return [];
  }, [appsRaw]);

  // active app = one in progress (or first). Every rung counts, not just the
  // first two: an application with the secretariat or the committee is still
  // this user's live application, and missing it here had the wizard fall back
  // to whatever happened to be first in the list.
  const activeApp = useMemo(() => {
    const list = apps;
    return (
      list.find((a) =>
        ["pending", "returned", ...IN_REVIEW, "approved"].includes(String(a.status)),
      ) ||
      list[0] ||
      null
    );
  }, [apps]);

  // compute "serverNextStep" from completion_step
  const serverNextStep = useMemo(() => {
    const completion = Number(activeApp?.completion_step || 0);
    return clamp(completion + 1, 1, 8);
  }, [activeApp?.completion_step]);

  // ✅ on page load / reload: always sync to serverNextStep (application level)
  useEffect(() => {
    if (!activeApp) {
      setApplicationId(undefined);
      setCurrentStep(1);
      return;
    }

    const id = String(activeApp.id);
    setApplicationId(id);

    // force currentStep into allowed range 1..serverNextStep
    setCurrentStep((prev) => clamp(prev || serverNextStep, 1, serverNextStep));
  }, [activeApp?.id, activeApp?.completion_step, serverNextStep]);

  // details endpoint (for hydration)
  const { data: appDetailsRaw, isFetching: detailsFetching } =
    useGetProbationerApplicationDetailsQuery(applicationId!, {
      skip: !applicationId,
    });

  const appDetails =
    (appDetailsRaw as unknown as ApplicationDetails | undefined) ?? undefined;

  // mutations
  const [step1Create, step1State] =
    useCreateProbationerApplicationStep1Mutation();
  const [step2Save, step2State] =
    useSaveProbationerStep2QualificationsMutation();
  const [step3Save, step3State] = useSaveProbationerStep3OLevelMutation();
  const [step4Save, step4State] = useSaveProbationerStep4MembershipsMutation();
  const [step5Save, step5State] = useSaveProbationerStep5ExperienceMutation();
  const [step6Save, step6State] = useSaveProbationerStep6SeminarsMutation();
  const [step7Save, step7State] = useSaveProbationerStep7RefereesMutation();
  const [step8Ack, step8State] = useAcknowledgeProbationerApplicationMutation();
  const [triggerMe] = useLazyMeQuery();

  const busy =
    categoriesFetching ||
    appsFetching ||
    detailsFetching ||
    step1State.isLoading ||
    step2State.isLoading ||
    step3State.isLoading ||
    step4State.isLoading ||
    step5State.isLoading ||
    step6State.isLoading ||
    step7State.isLoading ||
    step8State.isLoading;

  // persist draft
  useEffect(() => {
    saveDraft({ currentStep, applicationId });
  }, [currentStep, applicationId]);

  // navigation rules:
  // allowed steps: 1..serverNextStep
  const canGoToStep = (step: number) => step >= 1 && step <= serverNextStep;

  const goToStep = (step: number) => {
    if (!canGoToStep(step)) return;
    setLocalError(null);
    setLocalSuccess(null);
    setCurrentStep(step);
  };

  const goBack = () => goToStep(currentStep - 1);
  const goNext = () => goToStep(currentStep + 1);

  // after successful submit: jump to next step immediately (but still clamp to max 8)
  const jumpNextAfterSuccess = (msg: string) => {
    setLocalError(null);
    setLocalSuccess(msg);
    setCurrentStep((prev) => clamp(prev + 1, 1, 8));
    window.setTimeout(() => setLocalSuccess(null), 700);
  };

  // ✅ confirm step from server (FIXED for list shapes)
  const confirmStep = async (expectedCompletion: number) => {
    const r: any = await refetch();
    const raw = r?.data;

    const list: ApplicationSummary[] = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data)
      ? raw.data
      : apps;

    const app =
      list.find((a) => String(a?.id) === String(applicationId)) ||
      list.find((a) =>
        ["pending", "returned", ...IN_REVIEW, "approved"].includes(String(a.status)),
      ) ||
      null;

    if (!app)
      throw new Error("Could not confirm your application on the server.");

    const step = Number(app.completion_step || 0);
    if (step < expectedCompletion) {
      throw new Error(
        `Server did not confirm step ${expectedCompletion} completion. Please try again.`,
      );
    }

    setApplicationId(String(app.id));
    return app;
  };

  const ensureAppId = () => {
    if (!applicationId)
      throw new Error("No applicationId yet. Please complete Step 1 first.");
    return applicationId;
  };

  // =========================
  // INITIAL VALUES (DETAILS -> STEPS)
  // =========================
  const step1Initial: Step1BioInitial | null = useMemo(() => {
    if (!appDetails) return null;

    return {
      surname: appDetails.surname ?? "",
      other_names: appDetails.other_names ?? "",
      title: appDetails.title ?? "",
      gender: appDetails.gender ?? "",
      postal_address: appDetails.postal_address ?? "",
      residential_address: appDetails.residential_address ?? "",
      email: appDetails.email ?? "",
      phone: appDetails.phone ?? "",
      date_of_birth: appDetails.date_of_birth ?? "",
      nationality: appDetails.nationality ?? "",

      application_category_id:
        appDetails.application_category_id ??
        appDetails.application_category?.id ??
        null,

      state_id: appDetails.state_id ?? null,
      local_government_id:
        appDetails.lga_id ?? appDetails.local_government_id ?? null,
      sector_id: appDetails.sector_id ?? null,
      chapter_id: appDetails.chapter_id ?? null,
      chapter_other: appDetails.chapter_other ?? "",

      pro_pic_url: toStorageUrl(appDetails.pro_pic_url ?? appDetails.pro_pic),
      signature_url: toStorageUrl(
        appDetails.signature_url ?? appDetails.signature,
      ),
    };
  }, [appDetails]);

  const step2Initial: { qualifications: QualificationRow[] } | null =
    useMemo(() => {
      if (!appDetails) return null;

      const qs = Array.isArray(appDetails.qualifications)
        ? appDetails.qualifications
        : [];

      return {
        qualifications: qs.map((q: any) => ({
          institution: q?.institution ?? "",
          qualification: q?.qualification ?? "",
          year: String(q?.year ?? ""),
          cert_url: toStorageUrl(
            q?.cert_file ?? q?.cert_url ?? q?.certificate_url ?? q?.certificate,
          ),
          cert_file: q?.cert_file ?? undefined,
        })),
      };
    }, [appDetails]);

  const step3Initial: Step3OLevelInitial | null = useMemo(() => {
    if (!appDetails) return null;

    const raw = Array.isArray(appDetails.o_level_results)
      ? appDetails.o_level_results
      : [];

    return {
      results: raw.map((r: any) => ({
        exam_type: (r?.exam_type ?? "waec") as "waec" | "neco" | "nabteb",
        exam_year: String(r?.exam_year ?? ""),
        exam_number: r?.exam_number ?? "",
        subjects: r?.subjects ?? {},
        result_file: r?.result_file ?? undefined,
      })),
    };
  }, [appDetails]);

  const step4Initial: { memberships: Step4MembershipRow[] } | null =
    useMemo(() => {
      if (!appDetails) return null;

      const ms = Array.isArray(appDetails.professional_memberships)
        ? appDetails.professional_memberships
        : [];

      return {
        memberships: ms.map((m: any) => ({
          body_name: m?.body_name ?? "",
          stage_passed: m?.stage_passed ?? "",
          certificate_path: m?.certificate_path ?? "",
        })),
      };
    }, [appDetails]);

  const step5Initial: { experiences: Step5ExperienceRow[] } | null =
    useMemo(() => {
      if (!appDetails) return null;

      const xs = Array.isArray(appDetails.work_experiences)
        ? appDetails.work_experiences
        : [];

      return {
        experiences: xs.map((x: any) => ({
          organization: x?.organization ?? "",
          position: x?.position ?? "",
          start_date: x?.start_date ?? "",
          end_date: x?.end_date ?? "",
          responsibilities: x?.responsibilities ?? "",
        })),
      };
    }, [appDetails]);

  const step6Initial: { seminars: Step6SeminarRow[] } | null = useMemo(() => {
    if (!appDetails) return null;

    const ss = Array.isArray(appDetails.seminars) ? appDetails.seminars : [];

    return {
      seminars: ss.map((s: any) => ({
        title: s?.title ?? "",
        date: s?.date ?? "",
        location: s?.location ?? "",
      })),
    };
  }, [appDetails]);

  const step7Initial: Step7RefereeInitial | null = useMemo(() => {
    if (!appDetails) return null;

    const rr = Array.isArray(appDetails.referees) ? appDetails.referees : [];

    const toMember = (r: any): SelectedMember | undefined => {
      const m = r?.member || r?.member_details || null;
      if (m?.id) {
        return {
          id: m.id,
          member_id: m?.member_id ?? String(m.id),
          surname: m?.surname,
          other_names: m?.other_names,
          email: m?.email,
          phone: m?.phone,
          membership_no: m?.membership_no,
        };
      }
      if (r?.member_id)
        return { id: r.member_id, member_id: String(r.member_id) };
      return undefined;
    };

    const a = rr[0] || {};
    const b = rr[1] || {};

    return {
      referees: [
        { member: toMember(a), relationship: a?.relationship ?? "" },
        { member: toMember(b), relationship: b?.relationship ?? "" },
      ],
    };
  }, [appDetails]);

  // =========================
  // SUBMITS (move forward on success)
  // =========================
  const onSubmitStep1 = async (payload: any) => {
    setLocalError(null);
    setLocalSuccess(null);

    try {
      const fd = new FormData();
      fd.append("surname", payload.surname);
      fd.append("other_names", payload.other_names);
      fd.append("title", payload.title);
      fd.append("gender", payload.gender);
      if (payload.postal_address)
        fd.append("postal_address", payload.postal_address);
      fd.append("residential_address", payload.residential_address);
      fd.append("email", payload.email ?? "");
      fd.append("phone", payload.phone ?? "");
      fd.append("date_of_birth", payload.date_of_birth);
      fd.append("nationality", payload.nationality);

      fd.append(
        "application_category_id",
        String(payload.application_category_id),
      );
      fd.append("sector_id", String(payload.sector_id));
      fd.append("state_id", String(payload.state_id));
      fd.append("local_government_id", String(payload.local_government_id));

      // chapter_id is null when user picks "Others"
      if (payload.chapter_id != null) {
        fd.append("chapter_id", String(payload.chapter_id));
      }
      if (payload.chapter_other) {
        fd.append("chapter_other", payload.chapter_other);
      }

      if (payload.pro_pic) fd.append("pro_pic", payload.pro_pic);
      if (payload.signature) fd.append("signature", payload.signature);

      const res: any = await step1Create(fd).unwrap();

      await confirmStep(1);
      jumpNextAfterSuccess(res?.message || "Step 1 saved successfully.");
    } catch (err: any) {
      const msg = safeMsg(err); if (msg) setLocalError(msg);
      setCurrentStep(1);
    }
  };

  const onSubmitStep2 = async (payload: any) => {
    setLocalError(null);
    setLocalSuccess(null);

    try {
      const id = ensureAppId();

      const fd = new FormData();
      payload.qualifications.forEach((q: any, idx: number) => {
        fd.append(`qualifications[${idx}][institution]`, q.institution);
        fd.append(`qualifications[${idx}][qualification]`, q.qualification);
        fd.append(`qualifications[${idx}][year]`, q.year);
        // Preserve an existing certificate when no new file is attached.
        if (q.cert_file) fd.append(`qualifications[${idx}][cert_file]`, q.cert_file);

        const f = payload.certFilesByIndex?.[idx];
        if (f) fd.append(`cert_files[${idx}]`, f);
      });

      const res: any = await step2Save({
        applicationId: id,
        body: fd,
      }).unwrap();

      await confirmStep(2);
      jumpNextAfterSuccess(res?.message || "Step 2 saved successfully.");
    } catch (err: any) {
      const msg = safeMsg(err); if (msg) setLocalError(msg);
    }
  };

  const onSubmitStep3 = async (
    resultsPayload: any[],
    resultFilesByIndex: Record<number, File | null> = {},
  ) => {
    setLocalError(null);
    setLocalSuccess(null);

    try {
      const id = ensureAppId();
      const fd = new FormData();
      fd.append("results", JSON.stringify(resultsPayload));
      Object.entries(resultFilesByIndex).forEach(([i, f]) => {
        if (f) fd.append(`result_files[${i}]`, f);
      });
      const res: any = await step3Save({
        applicationId: id,
        body: fd,
      }).unwrap();

      await confirmStep(3);
      jumpNextAfterSuccess(res?.message || "Step 3 saved successfully.");
    } catch (err: any) {
      const msg = safeMsg(err); if (msg) setLocalError(msg);
    }
  };

  const onSubmitStep4 = async (memberships: any[]) => {
    setLocalError(null);
    setLocalSuccess(null);

    try {
      const id = ensureAppId();
      const res: any = await step4Save({
        applicationId: id,
        memberships,
      }).unwrap();

      jumpNextAfterSuccess(res?.message || "Step 4 saved successfully.");
    } catch (err: any) {
      const msg = safeMsg(err); if (msg) setLocalError(msg);
    }
  };

  const onSubmitStep5 = async (experiences: any[]) => {
    setLocalError(null);
    setLocalSuccess(null);

    try {
      const id = ensureAppId();
      const res: any = await step5Save({
        applicationId: id,
        experiences,
      }).unwrap();

      jumpNextAfterSuccess(res?.message || "Step 5 saved successfully.");
    } catch (err: any) {
      const msg = safeMsg(err); if (msg) setLocalError(msg);
    }
  };

  const onSubmitStep6 = async (seminars: any[]) => {
    setLocalError(null);
    setLocalSuccess(null);

    try {
      const id = ensureAppId();
      const res: any = await step6Save({
        applicationId: id,
        seminars,
      }).unwrap();

      jumpNextAfterSuccess(res?.message || "Step 6 saved successfully.");
    } catch (err: any) {
      const msg = safeMsg(err); if (msg) setLocalError(msg);
    }
  };

  const onSubmitStep7 = async (referees: any[]) => {
    setLocalError(null);
    setLocalSuccess(null);

    try {
      const id = ensureAppId();
      const res: any = await step7Save({
        applicationId: id,
        referees,
      }).unwrap();

      await confirmStep(7);
      jumpNextAfterSuccess(res?.message || "Step 7 saved successfully.");
    } catch (err: any) {
      const msg = safeMsg(err); if (msg) setLocalError(msg);
    }
  };

  const onSubmitStep8 = async () => {
    setLocalError(null);
    setLocalSuccess(null);

    try {
      const id = ensureAppId();
      const res: any = await step8Ack({ applicationId: id }).unwrap();

      await confirmStep(8);

      setLocalSuccess(res?.message || "Application submitted successfully!");

      // Write updated status to localStorage so the onboarding guard passes
      // immediately — guard checks has_application AND application_status !== "pending"
      const newStatus =
        res?.data?.status ?? res?.application?.status ?? "acknowledged";
      const storageKey = "logbook_auth_user_v2";
      const raw = localStorage.getItem(storageKey);
      const current = raw ? JSON.parse(raw) : {};
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          ...current,
          has_application: true,
          application_status: newStatus,
        }),
      );
      window.dispatchEvent(new Event("auth:updated"));

      // Sync fresh user data from DB so the guard's useMeQuery cache is also
      // up-to-date before we navigate — prevents the guard from re-blocking
      await triggerMe();

      clearDraft();
      navigate(`/probationer-applications/${id}`);
    } catch (err: any) {
      const msg = safeMsg(err); if (msg) setLocalError(msg);
    }
  };

  // clamp currentStep always (if serverNextStep decreased or reload)
  useEffect(() => {
    setCurrentStep((prev) => clamp(prev, 1, serverNextStep));
  }, [serverNextStep]);

  const canGoBack = currentStep > 1;
  const canGoForward = currentStep < 8 && currentStep < serverNextStep;

  // Submitted — on any rung past the applicant's own hands, not just the first.
  const isAcknowledged =
    IN_REVIEW.includes(String(appDetails?.status)) ||
    IN_REVIEW.includes(String(activeApp?.status));

  const categoryName = useMemo(
    () =>
      categories.find((c) => c.id === Number(user?.user_category_id))?.name ??
      "Membership Application",
    [categories, user?.user_category_id],
  );

  // Established members who already have a *completed* application don't belong
  // on the form. But members with no application (legacy/imported) — or one
  // that's still pending/returned — must be allowed through; the OnboardingGuard
  // routes them here on purpose. Redirecting them all back to /dashboard here
  // caused an infinite bounce (guard → /first-application → wizard → /dashboard).
  const hasCompletedApplication = apps.some((a) =>
    [...IN_REVIEW, "approved"].includes(String(a.status)),
  );
  if (user?.member_id && hasCompletedApplication) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-8">

      {/* ── Dark header card ───────────────────────────────────────────── */}
      <div className="mb-6 p-6 rounded-[32px] bg-gray-900 text-white shadow-xl space-y-5 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 p-8 opacity-[0.04]">
          <ClipboardList className="w-40 h-40" />
        </div>

        {/* Title row */}
        <div className="flex items-start justify-between gap-4 relative z-10">
          <div>
            <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-1">
              Membership Application
            </div>
            <h1 className="text-2xl font-black text-white leading-tight">
              {categoryName}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <p className="text-sm text-gray-400">
                Step <span className="text-white font-bold">{currentStep}</span> of 8
              </p>
              {isAcknowledged && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-[10px] font-bold uppercase tracking-wider">
                  ✓ Submitted — View Only
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                clearDraft();
                setLocalError(null);
                setLocalSuccess(null);
                setApplicationId(undefined);
                setCurrentStep(1);
              }}
              className="px-3 py-2 rounded-xl border border-white/10 text-xs font-bold text-white/50 hover:text-white hover:border-white/30 transition-all disabled:opacity-30"
            >
              Reset
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => { signOut(); navigate("/login", { replace: true }); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-500/30 text-xs font-bold text-red-400 hover:bg-red-500/10 hover:border-red-500/50 transition-all disabled:opacity-30"
            >
              <LogOut className="w-3 h-3" />
              Log out
            </button>
          </div>
        </div>

        {/* Step pills */}
        <div className="flex items-center gap-2 flex-wrap relative z-10">
          {Array.from({ length: 8 }).map((_, i) => {
            const step = i + 1;
            const allowed = canGoToStep(step);
            const done = step < currentStep;
            return (
              <button
                key={step}
                type="button"
                onClick={() => goToStep(step)}
                disabled={!allowed}
                title={allowed ? `Go to step ${step}` : "Complete previous steps first"}
                className={[
                  "h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-all",
                  step === currentStep
                    ? "bg-white text-gray-900 shadow-lg"
                    : done
                    ? "bg-white/20 text-white"
                    : allowed
                    ? "bg-white/10 text-white/50 hover:bg-white/20 hover:text-white"
                    : "bg-white/5 text-white/20 cursor-not-allowed",
                ].join(" ")}
              >
                {step}
              </button>
            );
          })}
        </div>

        {/* Back / Next nav */}
        <div className="flex items-center justify-between pt-3 border-t border-white/10 relative z-10">
          <button
            type="button"
            onClick={goBack}
            disabled={!canGoBack || busy}
            className="flex items-center gap-2 text-sm font-bold text-white/50 hover:text-white disabled:opacity-25 transition-all"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={!canGoForward || busy}
            className="flex items-center gap-2 text-sm font-bold text-white/50 hover:text-white disabled:opacity-25 transition-all"
          >
            Next <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {categoriesErrMsg && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
          {categoriesErrMsg}
        </div>
      )}

      {localSuccess && (
        <div className="mb-4 p-3 rounded-xl bg-green-50 border border-green-200 text-green-800 text-sm">
          {localSuccess}
        </div>
      )}

      {localError && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {localError}
        </div>
      )}

      <div className="bg-white border rounded-xl p-4 md:p-6">
        {currentStep === 1 && (
          <Step1Bio
            onSubmit={onSubmitStep1}
            busy={busy}
            initialValues={step1Initial}
            categories={categories}
            readOnly={isAcknowledged}
          />
        )}

        {currentStep === 2 && (
          <Step2Qualifications
            onSubmit={onSubmitStep2}
            busy={busy}
            onBack={goBack}
            initialValues={step2Initial}
            readOnly={isAcknowledged}
          />
        )}

        {currentStep === 3 && (
          <Step3OLevel
            onSubmit={onSubmitStep3}
            busy={busy}
            onBack={goBack}
            initialValues={step3Initial}
            readOnly={isAcknowledged}
          />
        )}

        {currentStep === 4 && (
          <Step4Memberships
            onSubmit={onSubmitStep4}
            busy={busy}
            onBack={goBack}
            initialValues={step4Initial}
            readOnly={isAcknowledged}
          />
        )}

        {currentStep === 5 && (
          <Step5Experience
            onSubmit={onSubmitStep5}
            busy={busy}
            onBack={goBack}
            initialValues={step5Initial}
            readOnly={isAcknowledged}
          />
        )}

        {currentStep === 6 && (
          <Step6Seminars
            onSubmit={onSubmitStep6}
            busy={busy}
            onBack={goBack}
            initialValues={step6Initial}
            readOnly={isAcknowledged}
          />
        )}

        {currentStep === 7 && (
          <Step7Referees
            onSubmit={onSubmitStep7}
            busy={busy}
            onBack={goBack}
            initialValues={step7Initial}
            readOnly={isAcknowledged}
          />
        )}

        {currentStep === 8 && (
          <Step8Acknowledge
            application={(appDetails as any) || (activeApp as any)}
            onSubmit={onSubmitStep8}
            busy={busy}
            onBack={goBack}
          />
        )}
      </div>
    </div>
  );
}
